// ReflectionPrompt.tsx — a reflection step. A private prompt + a place to write.
// Answers are kept on-device (never shared). Writing is optional — the step can
// be completed blank ("Skip for now").
import React from 'react';
import { Body, Meta, Pressable, Card, Btn, Display } from '@/components/ui';
import { Icon } from '@/components/icons';
import { FavouriteButton } from '@/components/FavouriteButton';
import { useProgress } from '@/hooks/useProgress';
import { storage } from '@/services/storage';
import { IN_APP_JOURNALING } from '@/config/features';
import { useForegroundDwell } from '@/hooks/useForegroundDwell';
import { nudgeFor, recordSkip, REFLECTION_SKIP_FLOOR_S, type ReflectionNudge } from '@/services/reflectionEngagement';
import type { Module, Step } from '@/types';
import { moduleName } from '@/services/contentProvider';

// A soft "sit with it" nudge shows at most once per app session. Module-level so
// it survives reflection remounts within a session, and resets on a fresh load.
let softNudgeShownThisLoad = false;

// An in-progress write is debounce-persisted under this key (per step), so a
// force-quit / backgrounded / accidental close never loses unsaved words. The
// draft is cleared once the reflection is kept (or skipped blank).
const draftKey = (stepId: string) => `renurture.reflection.draft.${stepId}`;

// One shared set of journalling guidance, lifted out of the lesson text (every
// journalling lesson used to repeat the same boilerplate). Collapsed by default
// so it never crowds the prompt.
const JOURNAL_NOTICE = ['thoughts that are emerging', 'emotions that are present', 'how your body feels', 'your reaction to what you’ve seen, heard or read'];

function JournalTips() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginTop: 16 }}>
      <Pressable
        as="button"
        onClick={() => setOpen((o) => !o)}
        ariaLabel="Journalling tips"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 40,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--green-deep)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 13.5,
          fontWeight: 600,
        }}
      >
        <Icon name={open ? 'chevDown' : 'chevRight'} size={16} /> Journalling tips
      </Pressable>
      {open && (
        <div
          style={{
            marginTop: 8,
            padding: '14px 16px',
            borderRadius: 16,
            background: 'rgba(29,158,117,0.06)',
            border: '1px solid rgba(29,158,117,0.16)',
          }}
        >
          <p style={{ margin: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink)' }}>
            Spend time with your journal, exploring your response to what you’ve just heard:
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--ink-soft)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.7 }}>
            {JOURNAL_NOTICE.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
          <p style={{ margin: '10px 0 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink)' }}>
            Write what you’re experiencing — don’t try to control it, just capture it. This is a marathon, not a race.
            Spend what time feels right; five minutes works for some, others prefer longer, and most people vary day to day.
          </p>
        </div>
      )}
    </div>
  );
}

export function ReflectionPrompt({
  step,
  module,
  initialNote,
  landingFollows,
  onSave,
  onComplete,
  onClose,
}: {
  step: Step;
  module: Module;
  initialNote: string;
  /** True when completing this step already shows a pause landing — so we don't
   *  stack the anti-skip nudge on top of it (the marked pause is the slow-down). */
  landingFollows?: boolean;
  /** Persist the written note (called with trimmed body; empty string clears). */
  onSave: (body: string, meta: { prompt: string; module: string; aha?: boolean }) => void;
  onComplete: () => void;
  onClose: () => void;
}) {
  const progress = useProgress();
  const inApp = IN_APP_JOURNALING && progress.state.inAppJournaling;
  // Prefer a recovered draft over the saved note — it's the most recent unsaved
  // work. Only restore when it actually differs from what was last saved.
  const [note, setNote] = React.useState(() => {
    const draft = storage.get(draftKey(step.id));
    return draft != null && draft !== initialNote ? draft : initialNote;
  });
  const [nudge, setNudge] = React.useState<ReflectionNudge>('none');
  const [confirmClose, setConfirmClose] = React.useState(false);
  const aha = !!step.aha;
  const journal = !!step.journal;
  const getDwell = useForegroundDwell();

  const shownPrompt = step.reflection_prompt ?? '';

  // Debounced autosave of the in-progress text (only when writing in-app). Clears
  // the draft when the text matches the saved note again (nothing to recover).
  React.useEffect(() => {
    if (!inApp) return;
    const t = window.setTimeout(() => {
      if (note === initialNote) storage.remove(draftKey(step.id));
      else storage.set(draftKey(step.id), note);
    }, 800);
    return () => window.clearTimeout(t);
  }, [note, initialNote, step.id, inApp]);

  // Finish the reflection: record engagement (dwell → practised/seen + skip window)
  // and advance. Anti-skip nudges never block — they only offer to help.
  const finish = (secs: number) => {
    progress.recordReflectionDwell(step.id, secs);
    onComplete();
  };
  const advance = () => {
    const secs = getDwell();
    // Always record engagement. But if a marked pause landing already follows this
    // step, don't also pop the anti-skip nudge — that would be two slow-downs in a row.
    if (landingFollows) {
      finish(secs);
      return;
    }
    const prospective = recordSkip(progress.state.reflectionSkips, secs < REFLECTION_SKIP_FLOOR_S);
    const decision = nudgeFor(secs, prospective, progress.state.reflectionOwnWay, softNudgeShownThisLoad);
    if (decision === 'none') {
      finish(secs);
      return;
    }
    if (decision === 'soft') softNudgeShownThisLoad = true;
    setNudge(decision);
  };
  const keep = () => {
    storage.remove(draftKey(step.id));
    onSave(note.trim(), { prompt: step.reflection_prompt ?? '', module: moduleName(module), aha });
    advance();
  };

  // Guard the header close: when writing in-app with unsaved edits, ask once
  // before discarding. The autosaved draft means an accidental close is still
  // recoverable on reopen, but the guard prevents the obvious footgun.
  const close = () => {
    const dirty = inApp && note.trim() !== initialNote.trim() && note.trim().length > 0;
    if (dirty && !confirmClose) {
      setConfirmClose(true);
      window.setTimeout(() => setConfirmClose(false), 4000);
      return;
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 82,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 260,
            background:
              'radial-gradient(120% 80% at 50% -10%, rgba(255,250,240,0.95), rgba(246,224,190,0.16) 44%, transparent 64%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            padding: 'calc(18px + var(--safe-top)) 18px 0',
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Pressable
            as="button"
            onClick={close}
            ariaLabel={confirmClose ? 'Discard unsaved changes' : 'Close'}
            style={
              confirmClose
                ? {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 999,
                    border: '1px solid var(--hair)',
                    background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                    color: '#b23b2e',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13.5,
                    fontWeight: 600,
                    boxShadow: 'var(--edge), var(--sh1)',
                  }
                : {
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    border: '1px solid var(--hair)',
                    background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                    color: 'var(--ink)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'var(--edge), var(--sh1)',
                  }
            }
          >
            <Icon name="chevLeft" size={20} /> {confirmClose ? 'Discard changes?' : ''}
          </Pressable>
          <FavouriteButton stepId={step.id} />
        </div>

        <div style={{ padding: '16px 26px 30px', position: 'relative' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              color: aha ? 'var(--amber-deep)' : 'var(--green-deep)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
              marginBottom: 15,
            }}
          >
            {aha ? (
              <>
                <Icon name="sparkle" size={16} /> Capture an Aha moment
              </>
            ) : journal ? (
              <>
                <Icon name="pencil" size={16} /> A journalling exercise
              </>
            ) : (
              <>
                <Icon name="reflections" size={16} /> A reflection
              </>
            )}
          </div>
          {step.image_url && (
            // The capture image is drawn for white, so it sits on a full-width
            // white band edge-to-edge (matching the lessons), not bare on cream.
            <div
              style={{
                background: '#fff',
                width: 'calc(100% + 52px)',
                marginLeft: -26,
                marginRight: -26,
                marginTop: 4,
                marginBottom: 20,
                padding: '18px 26px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src={step.image_url}
                alt=""
                aria-hidden="true"
                style={{ display: 'block', maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
              />
            </div>
          )}
          {shownPrompt && (
            <div style={{ borderLeft: '2px solid var(--amber)', paddingLeft: 16, marginTop: 4 }}>
              {shownPrompt.split('\n\n').map((para, i) => (
                <p
                  key={i}
                  style={{
                    margin: i === 0 ? 0 : '14px 0 0',
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontStyle: 'italic',
                    // Reading text — must meet WCAG AA (4.5:1). Amber-deep on cream is
                    // only 4.30:1 and weight 300 reads thinner still, so the prompt
                    // (the words the user has to read and act on) uses ink at 400.
                    // The amber rule on the left keeps the warm prompt identity.
                    fontWeight: 400,
                    fontSize: 20,
                    lineHeight: 1.55,
                    color: 'var(--ink)',
                  }}
                >
                  {para}
                </p>
              ))}
            </div>
          )}
          {journal && <JournalTips />}
          {inApp ? (
            <>
              <div style={{ marginTop: 22 }}>
                <Card well pad={3} radius={18}>
                  <textarea
                    value={note}
                    onChange={(ev) => setNote(ev.target.value)}
                    aria-label="Your reflection"
                    placeholder="Write as much or as little as you like. You can leave it blank."
                    style={{
                      width: '100%',
                      minHeight: 168,
                      resize: 'none',
                      boxSizing: 'border-box',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 16,
                      padding: 15,
                      color: 'var(--ink)',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 16,
                      lineHeight: 1.6,
                      outline: 'none',
                    }}
                  />
                </Card>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12 }}>
                <span style={{ color: 'var(--green-deep)' }}>
                  <Icon name="shield" size={15} />
                </span>
                <Meta>Private — kept on your device, never shared</Meta>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 22, padding: '16px 17px', borderRadius: 16, border: '1px solid rgba(29,158,117,0.18)', background: 'rgba(29,158,117,0.05)', display: 'flex', gap: 12 }}>
              <span style={{ color: 'var(--green-deep)', flexShrink: 0, marginTop: 1 }}>
                <Icon name="book" size={20} />
              </span>
              <div>
                <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, color: 'var(--ink)', marginBottom: 5 }}>
                  Take this to your journal
                </div>
                <Body dim size={13.5}>
                  When you have the time and space, write your response in your own journal — in your own
                  hand, at your own pace. There is no rush.
                </Body>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          padding: '14px 26px calc(22px + var(--safe-bottom))',
          borderTop: '1px solid var(--hair)',
          background: 'linear-gradient(180deg, transparent, var(--cream) 30%)',
        }}
      >
        {inApp ? (
          <Btn full size="lg" onClick={keep}>
            {note.trim() ? 'Keep this reflection' : 'Skip for now'}
          </Btn>
        ) : (
          <Btn full size="lg" onClick={advance}>
            Complete
          </Btn>
        )}
      </div>

      {nudge !== 'none' && (
        <ReflectionNudgeSheet
          variant={nudge}
          onSit={() => setNudge('none')}
          onProceed={() => {
            setNudge('none');
            finish(getDwell());
          }}
          onComeBack={() => {
            setNudge('none');
            onClose();
          }}
          onOwnWay={() => {
            progress.setReflectionOwnWay();
            setNudge('none');
            finish(getDwell());
          }}
        />
      )}
    </div>
  );
}

// The anti-skip prompt. Soft = a one-tap invitation to sit with it. Escalate =
// a warmer offer of help (shorter prompts / come back / "my own way"). It never
// shows or implies time, and never disapproves — it leans in to help.
function ReflectionNudgeSheet({
  variant,
  onSit,
  onProceed,
  onComeBack,
  onOwnWay,
}: {
  variant: ReflectionNudge;
  onSit: () => void;
  onProceed: () => void;
  onComeBack: () => void;
  onOwnWay: () => void;
}) {
  return (
    <div
      style={{ position: 'absolute', inset: 0, zIndex: 90, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(28,22,16,0.32)', animation: 'fade .2s ease' }}
      onClick={variant === 'soft' ? onProceed : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--cream)', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: '22px 24px calc(24px + var(--safe-bottom))', boxShadow: '0 -12px 40px -12px rgba(0,0,0,0.35)', animation: 'rise .3s cubic-bezier(.2,.7,.3,1)' }}
      >
        <div style={{ width: 38, height: 5, borderRadius: 999, background: 'var(--hair-strong)', margin: '0 auto 16px' }} />
        {variant === 'soft' ? (
          <>
            <Display parts={{ pre: 'Sit with it a ', em: 'moment', post: '?' }} size={21} lh={1.18} />
            <Body size={14} style={{ margin: '10px 0 18px', lineHeight: 1.6 }}>
              These tend to land better when you give them a little time. No pressure — only if it helps.
            </Body>
            <Btn full size="lg" onClick={onSit} leading={<Icon name="leaf" size={18} />}>
              Okay, I’ll sit with it
            </Btn>
            <Btn full variant="ghost" onClick={onProceed} style={{ marginTop: 10 }}>
              I’m good — continue
            </Btn>
          </>
        ) : (
          <>
            <Display parts={{ pre: 'No need to ', em: 'rush', post: ' it.' }} size={21} lh={1.18} />
            <Body size={14} style={{ margin: '10px 0 18px', lineHeight: 1.6 }}>
              These get rushed when the material’s heavy or you’re short on time — that’s completely human.
              You can always come back when you’ve more space for it.
            </Body>
            <Btn full size="lg" onClick={onComeBack} leading={<Icon name="leaf" size={18} />}>
              I’ll come back to this
            </Btn>
            <Btn full variant="ghost" onClick={onOwnWay} style={{ marginTop: 10 }}>
              I’m doing these my own way
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
